/**
 * SalesDealDetail.tsx — Sales / per-deal detail page (Deal Detail v1).
 *
 * Spec: docs/design/sales-build-design.md §3 ("Deal Detail plan"), decisions
 * D3-1a (tasks via sales_activities type 'task'), D3-2 (timeline = manual
 * entries + two labelled SYNTHESIZED entries — no auto-logging exists yet),
 * D3-4 (Emails omitted — no backing), D3-5 superseded by slice 3a (§5.E):
 * the close command is live, so the header carries Close as Won / Close as
 * Lost actions into the shared CloseDealModal; the funnel's closed step
 * stays display-only (closing is a ceremony with a consequence, not a
 * funnel click), and a won deal's minted draft renders as an Invoice row.
 *
 * The deal is fetched FRESH by id (never rendered from a stale card copy);
 * mutation responses land in a keyed override so no fetched-state is copied
 * into local state (no setState-in-effect).
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState } from 'react'
import {
  ArrowLeft, Phone, Mail, Users, FileText, Plus, Check,
  Calendar, Tag, Flag, Layers, User, Building2, Link2, Pencil, Lock,
} from 'lucide-react'
import { Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { DealDetail, DealStage } from '../../types'
import type { SalesActivity as WireActivity } from '../../../../api/types'
import {
  useAsync, fetchDeal, fetchContact, fetchDealActivities, linkDealAccount,
  createDealActivity, completeActivity, reopenActivity,
  updateDealNotes, updateDeal, moveDealStage, mapDealDetail, getIssuedInvoice,
  STAGE_TO_SLUG, relAgo, fmtDate,
} from '../../api'
import { CloseDealModal } from './CloseDealModal'
import { SalesAuditStrip } from './SalesAuditStrip'
import { AccountPicker, type AccountPick } from './AccountPicker'
import { OwnerSelect } from './OwnerSelect'
import { CurrencyGlyph, CurrencySelect } from '../../CurrencySelect'
import type { CloseOutcome } from './closeFlow'
import { visibleSignals } from './dealHealth'
import { DealHealthBadge } from './DealHealthBadge'
import { lostReasonLabel, sourceLabel, SOURCE_OPTIONS, PRIORITY_OPTIONS, FORECAST_CATEGORY_PICKER, forecastCategoryLabel } from './vocab'
import {
  draftFromDeal, dealEditDiff, dealEditSavable, fieldEditable, isClosedStage,
  probabilityInputValid, probabilitySourceLabel,
  FROZEN_REASON, type DealEditDraft,
} from './dealEdit'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { accent, danger, stageDot } from '../../../../theme/tokens'
import { money, fxProvenance } from '../../../../lib/money'

// The mockup's 5-step funnel (§A.3, klaxiora-crm-mockup.html:668-674) —
// Closed Lost is an exit, not a step.
const FUNNEL: DealStage[] = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation', 'Closed Won']
// The stages a click may move a deal INTO (decision #7: closed stages only
// via the close command — the header Close actions / CloseDealModal).
const OPEN_STAGES: DealStage[] = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation']

const LOG_TYPES = ['note', 'call', 'meeting', 'email'] as const

const PRIORITY_LABEL: Record<string, string> = { low: 'Low', medium: 'Medium', high: 'High' }

function PropRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-edge-row last:border-0">
      <div className="text-12 text-ink-muted font-medium">{label}</div>
      <div className="text-13 text-ink-primary font-medium text-right">{children}</div>
    </div>
  )
}

// Slice E (E2.3): a field that is FROZEN on a closed deal, in edit mode. It
// renders its stored value plus the one-line reason — no input is offered, so
// nobody types into a field the server would refuse. The E-2 409 is the
// backstop for API callers, never the UX.
function FrozenValue({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span className="inline-flex items-center gap-1 text-ink-secondary"><Lock size={11} className="text-ink-faint" />{children}</span>
      <span className="text-11 text-ink-faint font-normal max-w-56 text-right">{FROZEN_REASON}</span>
    </span>
  )
}

// ── Notes card (keyed by deal id — remount resets the draft) ─────────────────

function NotesCard({ deal, onSaved }: { deal: DealDetail; onSaved: (d: DealDetail) => void }) {
  const [draft, setDraft] = useState(deal.notes)
  const [saving, setSaving] = useState(false)
  // An emptied box is a SILENT NO-OP on today's partial-update wire (notes
  // cannot be cleared until the clear-to-null slice) — so an empty draft
  // never offers a save.
  const savable = draft.trim() !== '' && draft !== deal.notes && !saving

  async function save() {
    setSaving(true)
    try {
      onSaved(await updateDealNotes(deal.id, draft))
      toast.success('Note saved')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to save note'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="k-card">
      <div className="k-card-head">
        <div className="k-card-title">Notes</div>
      </div>
      <div className="p-5 flex flex-col gap-3">
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Add a note about this deal..."
          rows={4}
          className="w-full px-4 py-3 border border-edge-control rounded-control text-13 text-ink-primary placeholder:text-ink-faint focus:outline-none focus:border-accent resize-none"
        />
        <div className="flex items-center justify-between">
          <span className="text-11 text-ink-faint">
            {deal.notes !== '' && draft.trim() === '' ? 'Notes cannot be emptied yet — type replacement text to save.' : ''}
          </span>
          <button onClick={save} disabled={!savable}
            className="k-btn disabled:opacity-50">
            {saving ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Primary contact card ──────────────────────────────────────────────────────

function ContactCard({ contactId }: { contactId?: string }) {
  const { data, loading, error } = useAsync(
    () => (contactId ? fetchContact(contactId) : Promise.resolve(null)),
    [contactId],
  )
  return (
    <div className="k-card">
      <div className="k-card-head">
        <div className="k-card-title">Primary contact</div>
      </div>
      <div className="p-5">
        {!contactId ? (
          <div className="text-13 text-ink-muted">No contact linked.</div>
        ) : loading ? (
          <div className="text-13 text-ink-muted">Loading…</div>
        ) : error || !data ? (
          <div className="text-13 text-ink-muted">Contact unavailable.</div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Avatar name={data.name} size={40} />
              <div className="min-w-0">
                <div className="text-14 font-semibold text-ink-primary truncate">{data.name}</div>
                <div className="text-12 text-ink-muted truncate">
                  {[data.title, data.company].filter(Boolean).join(', ') || '—'}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 text-12 text-ink-secondary">
              <div className="flex items-center gap-2"><Mail size={13} className="text-ink-faint" />{data.email || '—'}</div>
              <div className="flex items-center gap-2"><Phone size={13} className="text-ink-faint" />{data.phone || '—'}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Invoice row (§5.E: a won deal links its minted draft; C5-3 = invoice
//    number + tab navigation — no per-invoice deep link exists) ───────────────

function InvoiceRow({ invoiceId, onNavigate }: { invoiceId: string; onNavigate?: (dest: string) => void }) {
  const { data, loading, error } = useAsync(() => getIssuedInvoice(invoiceId), [invoiceId])
  return (
    <PropRow label="Invoice">
      {loading ? 'Loading…' : error || !data ? 'Unavailable' : onNavigate ? (
        <button type="button" onClick={() => onNavigate('finance.issued-invoices')}
          title="View in Issued Invoices"
          className="font-medium hover:underline" style={{ color: accent.DEFAULT }}>
          {data.invoiceNumber}
        </button>
      ) : (
        data.invoiceNumber
      )}
    </PropRow>
  )
}

// ── Timeline entry shape (manual wire rows + synthesized derived rows) ────────

interface TimelineEntry {
  key: string
  icon: React.ReactNode
  title: string
  body: string
  at: string // ISO, for ordering
  derived: boolean
}

const TYPE_ICON: Record<string, React.ReactNode> = {
  call: <Phone size={13} />,
  email: <Mail size={13} />,
  meeting: <Users size={13} />,
  note: <FileText size={13} />,
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SalesDealDetail({ dealId, onBack, backLabel, onNavigate, onOpenAccount }: {
  dealId: string
  onBack: () => void
  backLabel: string
  onNavigate?: (dest: string) => void
  /** Slice A: opens the account 360 for a deal that carries an account_id. */
  onOpenAccount?: (accountId: string) => void
}) {
  const dealQ = useAsync(() => fetchDeal(dealId), [dealId])
  const actsQ = useAsync(() => fetchDealActivities(dealId), [dealId])

  // Mutation responses override the fetched deal (keyed — never copied into
  // state from the fetch itself). Activity adds/patches merge the same way.
  const [override, setOverride] = useState<{ id: string; deal: DealDetail } | null>(null)
  const [added, setAdded] = useState<WireActivity[]>([])
  const [patched, setPatched] = useState<Record<string, WireActivity>>({})
  const [moving, setMoving] = useState(false)
  // The close ceremony (§5.E) — preset outcome, shared CloseDealModal.
  const [closing, setClosing] = useState<CloseOutcome | null>(null)
  const [newTask, setNewTask] = useState('')
  const [logType, setLogType] = useState<(typeof LOG_TYPES)[number]>('note')
  const [logText, setLogText] = useState('')
  const [logging, setLogging] = useState(false)
  // Slice A (A2.3): the link-an-account row under Deal properties.
  const [linking, setLinking] = useState(false)
  const [linkPick, setLinkPick] = useState<AccountPick>({ accountId: '', company: '' })
  // Slice E (E-7): the properties card's in-place edit — the Account Detail
  // header-edit precedent. The draft is built from the STORED deal on Edit,
  // the PUT carries only the diff (E2.2), and frozen fields on a closed deal
  // are never offered as inputs (E2.3). Declared here, before the early
  // returns below, like every other hook in this component.
  const [editingProps, setEditingProps] = useState(false)
  const [propsDraft, setPropsDraft] = useState<DealEditDraft | null>(null)
  const [savingProps, setSavingProps] = useState(false)

  const deal = override && override.id === dealId ? override.deal : dealQ.data

  if (dealQ.loading) return <LoadingState />
  if (dealQ.error || !deal) return <ErrorState message={dealQ.error ?? 'Deal not found'} />

  const applyDeal = (d: DealDetail) => setOverride({ id: dealId, deal: d })

  const activities = [
    ...added.filter(a => a.dealId === dealId),
    ...(actsQ.data ?? []),
  ].map(a => patched[a.id] ?? a)

  const tasks = activities.filter(a => a.activityType === 'task')
  const manual = activities.filter(a => a.activityType !== 'task')

  const isClosed = deal.stage === 'Closed Won' || deal.stage === 'Closed Lost'
  const currentIdx = FUNNEL.indexOf(deal.stage) // -1 for Closed Lost / Unknown

  // Slice A: the company leads the sub-line and LINKS to the account 360 when
  // the deal carries an account_id; unlinked deals keep the plain text.
  const linkedAccount = deal.accountId
  const subTail = [
    deal.source ? `Sourced from ${deal.source}` : '',
    deal.owner ? `Owned by ${deal.owner}` : '',
  ].filter(Boolean)

  // Timeline: manual entries + the two SYNTHESIZED entries (D3-2). Only the
  // LAST stage change is knowable (stage_changed_at) — full history needs the
  // auto-logging slice. Both derived entries are labelled as derived.
  const entries: TimelineEntry[] = manual.map(a => ({
    key: a.id,
    icon: TYPE_ICON[a.activityType] ?? <FileText size={13} />,
    title: a.subject,
    body: a.body ?? '',
    at: a.createdAt,
    derived: false,
  }))
  if (deal.stageChangedAt && deal.stageChangedAt !== deal.createdAt) {
    entries.push({
      key: 'synth-stage',
      icon: <Layers size={13} />,
      title: `Entered ${deal.stage}`,
      body: 'Derived from the deal record — only the most recent stage change is known.',
      at: deal.stageChangedAt,
      derived: true,
    })
  }
  entries.push({
    key: 'synth-created',
    icon: <Plus size={13} />,
    title: 'Deal created',
    body: 'Derived from the deal record.',
    at: deal.createdAt,
    derived: true,
  })
  entries.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))

  async function moveTo(stage: DealStage) {
    if (moving || isClosed || stage === deal!.stage || !OPEN_STAGES.includes(stage)) return
    const slug = STAGE_TO_SLUG[stage]
    if (!slug) return
    setMoving(true)
    try {
      applyDeal(mapDealDetail(await moveDealStage(dealId, slug)))
      toast.success(`Moved to ${stage}`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to move stage'))
    } finally {
      setMoving(false)
    }
  }

  async function addTask() {
    const subject = newTask.trim()
    if (!subject) return
    try {
      const created = await createDealActivity({ dealId, activityType: 'task', subject })
      setAdded(prev => [created, ...prev])
      setNewTask('')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to add task'))
    }
  }

  async function toggleTask(t: WireActivity) {
    const done = t.status === 'done' || Boolean(t.completedAt)
    try {
      const updated = done ? await reopenActivity(t.id) : await completeActivity(t.id)
      setPatched(prev => ({ ...prev, [t.id]: updated }))
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update task'))
    }
  }

  async function logUpdate() {
    const subject = logText.trim()
    if (!subject || logging) return
    setLogging(true)
    try {
      const created = await createDealActivity({
        dealId, activityType: logType, subject,
        ...(deal!.contactId ? { contactId: deal!.contactId } : {}),
      })
      setAdded(prev => [created, ...prev])
      setLogText('')
      toast.success('Update logged')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to log update'))
    } finally {
      setLogging(false)
    }
  }

  // A2.3: picking in the row links immediately — PUT /deals/{id} with the
  // accountId and the name copied into company; the response replaces the deal.
  async function linkAccount(p: AccountPick) {
    setLinkPick(p)
    if (!p.accountId) return
    try {
      applyDeal(await linkDealAccount(dealId, p.accountId, p.company))
      setLinking(false)
      toast.success(`Linked to ${p.company}`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to link account'))
    }
  }

  // Slice E (E2.2): ONE PUT carrying only the diff between the stored deal
  // and the draft — so one intended edit yields one history row per field
  // actually changed. Save is disabled when the diff is empty, so a no-op
  // save cannot be sent. The response replaces the deal (keyed override).
  async function savePropsEdit() {
    if (!propsDraft || !deal) return
    const body = dealEditDiff(deal, propsDraft)
    if (Object.keys(body).length === 0) return
    setSavingProps(true)
    try {
      applyDeal(await updateDeal(dealId, body))
      setEditingProps(false)
      setPropsDraft(null)
      toast.success('Deal updated')
    } catch (e) {
      // The E-2 409 is a real sentence (the C-10 standard) and surfaces
      // verbatim; it is reachable here only if the deal closed underneath
      // an open editor, since frozen fields are never offered on a closed deal.
      toast.error(apiErrorMessage(e, 'Failed to update deal'))
    } finally {
      setSavingProps(false)
    }
  }

  const inputCls = 'h-9 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'

  // 2.4 (P2-12): every server signal, worst first — [] / null / absent all
  // give an empty list, so a healthy or closed deal shows no chip at all.
  const signals = visibleSignals(deal.health)

  return (
    <div className="page-enter flex flex-col gap-4">
      {/* Back link */}
      <button onClick={onBack}
        className="flex items-center gap-2 text-12 font-medium text-ink-secondary hover:text-ink-primary transition-colors self-start">
        <ArrowLeft size={14} /> {backLabel}
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-section-title font-semibold text-ink-primary leading-tight">{deal.title}</h1>
          <p className="text-13 text-ink-secondary mt-1">
            {deal.company && (linkedAccount && onOpenAccount ? (
              <button type="button" onClick={() => onOpenAccount(linkedAccount)} title="Open account"
                className="font-medium hover:underline" style={{ color: accent.DEFAULT }}>
                {deal.company}
              </button>
            ) : deal.company)}
            {subTail.map((t, i) => <span key={t}>{i > 0 || deal.company ? ' · ' : ''}{t}</span>)}
          </p>
        </div>
        <div className="text-right">
          <div className="text-overline text-ink-faint uppercase tracking-wider mb-1">Deal value</div>
          <div className="text-section-title font-bold text-ink-primary">{money(deal.amountPence / 100, deal.currency)}</div>
          {/* FX provenance (SB-6): individual figures only — the captured
              base and its fixing date; '' (GBP / pre-capture) renders nothing. */}
          {fxProvenance(deal.baseValuePence, deal.fxRateDate) !== '' && (
            <div className="text-11 text-ink-faint mt-0.5">{fxProvenance(deal.baseValuePence, deal.fxRateDate)}</div>
          )}
          {/* Close actions (§5.E / F2) — open stages only; both land in the
              shared CloseDealModal with the outcome preset. */}
          {!isClosed && (
            <div className="flex items-center gap-2 mt-2 justify-end">
              <button className="k-btn-secondary" onClick={() => setClosing('won')}>Close as Won</button>
              <button className="k-btn-secondary" onClick={() => setClosing('lost')}>Close as Lost</button>
            </div>
          )}
        </div>
      </div>

      {/* Stage bar — funnel per the mockup; open moves live. The Closed Won
          step stays display-only: closing is the ceremony behind the header
          Close actions (§5.E), not a funnel click. Closed Lost renders as an
          exit pill. */}
      <div className="flex items-center gap-2 flex-wrap">
        {FUNNEL.map((s, i) => {
          const isCurrent = s === deal.stage
          const done = currentIdx >= 0 && i < currentIdx
          const clickable = !isClosed && !moving && !isCurrent && OPEN_STAGES.includes(s)
          return (
            <button key={s} type="button" disabled={!clickable}
              onClick={() => moveTo(s)}
              className={`px-4 py-2 rounded-full text-12 font-semibold border transition-all ${isCurrent ? 'border-transparent text-ink-on-accent' : done ? 'border-edge-control bg-surface-panel text-ink-body' : 'border-edge-control bg-surface-control text-ink-muted'} ${clickable ? 'hover:border-ink-disabled cursor-pointer' : 'cursor-default'}`}
              style={isCurrent ? { background: accent.DEFAULT } : {}}>
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: stageDot[s] }} />
                {s}{done ? ' ✓' : ''}
              </span>
            </button>
          )
        })}
        {deal.stage === 'Closed Lost' && (
          <span className="px-4 py-2 rounded-full text-12 font-semibold text-ink-on-accent"
            style={{ background: danger.DEFAULT }}>
            Closed Lost
          </span>
        )}
        <span className="text-11 text-ink-faint ml-1">{deal.daysInStage}d in stage</span>
        {/* 2.4: the signal list beside the age — label AND since, so a rep
            reads "stale since 29 Aug" rather than a bare warning colour. */}
        {signals.length > 0 && (
          <span className="inline-flex items-center gap-2 flex-wrap ml-1" aria-label="Deal health signals">
            {signals.map(s => <DealHealthBadge key={`${s.code}:${s.since}`} signal={s} since />)}
          </span>
        )}
      </div>

      {/* Two-column body: Notes / Tasks / Timeline | Properties / Contact */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <NotesCard key={deal.id} deal={deal} onSaved={applyDeal} />

          {/* Tasks — sales_activities type 'task' (D3-1a; no deal_tasks table) */}
          <div className="k-card">
            <div className="k-card-head">
              <div>
                <div className="k-card-title">Tasks</div>
                {actsQ.error && <div className="k-card-sub">Tasks unavailable</div>}
              </div>
            </div>
            <div className="p-5 flex flex-col gap-3">
              {tasks.length === 0 && !actsQ.loading && (
                <div className="text-13 text-ink-muted">No tasks yet.</div>
              )}
              {tasks.map(t => {
                const done = t.status === 'done' || Boolean(t.completedAt)
                return (
                  <button key={t.id} type="button" onClick={() => toggleTask(t)}
                    className="flex items-center gap-3 text-left group">
                    <span className={`w-5 h-5 rounded flex items-center justify-center border transition-colors flex-shrink-0 ${done ? 'border-transparent text-ink-on-accent' : 'border-edge-control bg-surface-control group-hover:border-ink-disabled'}`}
                      style={done ? { background: accent.DEFAULT } : {}}>
                      {done && <Check size={12} />}
                    </span>
                    <span className={`text-13 ${done ? 'text-ink-muted line-through' : 'text-ink-body'}`}>{t.subject}</span>
                  </button>
                )
              })}
              <div className="flex items-center gap-2 pt-1">
                <input value={newTask} onChange={e => setNewTask(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') void addTask() }}
                  placeholder="Add a task…" className={`${inputCls} flex-1`} />
                <button onClick={addTask} disabled={!newTask.trim()} aria-label="Add task"
                  className="k-btn-secondary k-btn-secondary--flex disabled:opacity-50">
                  <Plus size={13} />
                </button>
              </div>
            </div>
          </div>

          {/* Activity timeline — manual entries + labelled derived entries
              (D3-2). Emails section omitted: no email backing (D3-4). */}
          <div className="k-card">
            <div className="k-card-head">
              <div>
                <div className="k-card-title">Activity timeline</div>
                {actsQ.error && <div className="k-card-sub">Logged activity unavailable — showing derived entries only</div>}
              </div>
            </div>
            <div className="p-5 flex flex-col gap-4">
              {/* Log an update */}
              <div className="flex items-center gap-2">
                <select value={logType} onChange={e => setLogType(e.target.value as (typeof LOG_TYPES)[number])}
                  className={`${inputCls} bg-surface-card`}>
                  {LOG_TYPES.map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
                </select>
                <input value={logText} onChange={e => setLogText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') void logUpdate() }}
                  placeholder="Log an update…" className={`${inputCls} flex-1`} />
                <button onClick={logUpdate} disabled={!logText.trim() || logging}
                  className="k-btn disabled:opacity-50">
                  {logging ? 'Logging…' : 'Log'}
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {entries.map(e => (
                  <div key={e.key} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-surface-panel border border-edge-row flex items-center justify-center text-ink-faint flex-shrink-0">
                      {e.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="text-13 font-semibold text-ink-primary flex items-center gap-2">
                        {e.title}
                        {e.derived && (
                          <span className="text-overline font-medium text-ink-faint bg-surface-panel rounded px-2 py-1">derived</span>
                        )}
                      </div>
                      {e.body && <div className="text-12 text-ink-secondary mt-1">{e.body}</div>}
                      <div className="text-11 text-ink-faint mt-1">{relAgo(e.at)} · {fmtDate(e.at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Slice B (0117): the audit strip. Deals are the one record type
              with stage transitions, so this is the only mount that asks for
              them — as a SECOND read, never a merged one (B-1). */}
          <SalesAuditStrip recordType="deals" recordId={dealId} withStageEvents />
        </div>

        <div className="flex flex-col gap-4">
          {/* Deal properties — the mockup's six rows (§A.3), plus the honest
              lost-reason row when the deal is lost. */}
          <div className="k-card">
            <div className="k-card-head">
              <div>
                <div className="k-card-title">Deal properties</div>
                {editingProps && isClosedStage(deal.stage) && (
                  <div className="k-card-sub">Closed deal — value, currency, expected close, priority, probability and forecast category are fixed</div>
                )}
              </div>
              {/* Slice E (E-7): the in-place edit, the Account Detail precedent.
                  Edit is offered on closed deals too — title, owner, source and
                  the account link stay editable there (E-2). */}
              {!editingProps ? (
                <button type="button" className="k-btn-quiet inline-flex items-center gap-1"
                  onClick={() => { setPropsDraft(draftFromDeal(deal)); setEditingProps(true) }}>
                  <Pencil size={12} /> Edit
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button type="button" className="k-btn-secondary" disabled={savingProps}
                    onClick={() => { setEditingProps(false); setPropsDraft(null) }}>Cancel</button>
                  <button type="button" className="k-btn disabled:opacity-60"
                    disabled={!propsDraft || !dealEditSavable(deal, propsDraft, savingProps)}
                    onClick={() => void savePropsEdit()}>
                    {savingProps ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            <div className="px-5 py-2">
              <PropRow label="Stage">
                <span className="inline-flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: stageDot[deal.stage] }} />
                  {deal.stage}
                </span>
              </PropRow>
              {editingProps && propsDraft ? (
                <>
                  {/* E2.2: every input edits the DRAFT; the PUT is the diff. E2.3: a
                      frozen field on a closed deal renders as its value + the
                      reason — no input is offered, so the 409 is unreachable
                      from here. */}
                  <PropRow label="Title">
                    <input value={propsDraft.title} onChange={e => setPropsDraft({ ...propsDraft, title: e.target.value })}
                      className={`${inputCls} w-56`} aria-label="Deal title" />
                  </PropRow>
                  <PropRow label="Value">
                    {fieldEditable('value', deal.stage) ? (
                      <span className="inline-flex items-center gap-2">
                        <CurrencySelect id="deal-edit-currency" value={propsDraft.currency}
                          onChange={c => setPropsDraft({ ...propsDraft, currency: c })} className={`${inputCls} w-24 bg-surface-card`} />
                        <input type="number" min="0.01" step="0.01" value={propsDraft.value}
                          onChange={e => setPropsDraft({ ...propsDraft, value: e.target.value })}
                          className={`${inputCls} w-32`} aria-label="Deal value" />
                      </span>
                    ) : (
                      <FrozenValue><CurrencyGlyph code={deal.currency} /> {money(deal.amountPence / 100, deal.currency)}</FrozenValue>
                    )}
                  </PropRow>
                  {/* 2.2 (P2-7): the deal's OWN probability. The placeholder is the
                      value it inherits today, never written into the input; blank +
                      save = "not assessed, inherit" (clearProbability). */}
                  <PropRow label="Probability">
                    {fieldEditable('probability', deal.stage) ? (
                      <span className="inline-flex flex-col items-end gap-0.5">
                        <span className="inline-flex items-center gap-1">
                          <input type="number" min={0} max={100} step={1} inputMode="numeric" value={propsDraft.probability}
                            placeholder={deal.effectiveProbability != null ? String(deal.effectiveProbability) : ''}
                            onChange={e => setPropsDraft({ ...propsDraft, probability: e.target.value })}
                            aria-invalid={!probabilityInputValid(propsDraft.probability) || undefined}
                            className={`${inputCls} w-24 text-right`} aria-label="Probability" />
                          <span className="text-12 text-ink-muted">%</span>
                        </span>
                        <span className="text-11 text-ink-faint font-normal max-w-56 text-right">
                          {propsDraft.probability.trim() === ''
                            ? (deal.effectiveProbability != null ? `Blank inherits ${deal.effectiveProbability}% from the stage ladder` : 'Blank inherits the stage ladder')
                            : 'Assessed on this deal'}
                        </span>
                      </span>
                    ) : (
                      <FrozenValue>{deal.effectiveProbability != null ? `${deal.effectiveProbability}%` : '—'}</FrozenValue>
                    )}
                  </PropRow>
                  {/* 2.3a (F-2 / F-3): the forecast category. Uncategorised is the
                      FIRST row and is not a fifth slug — picking it clears the
                      field (clearForecastCategory), which is why its value is ''.
                      Frozen on a closed deal like probability. */}
                  <PropRow label="Forecast category">
                    {fieldEditable('forecastCategory', deal.stage) ? (
                      <span className="inline-flex flex-col items-end gap-0.5">
                        <select value={propsDraft.forecastCategory}
                          onChange={e => setPropsDraft({ ...propsDraft, forecastCategory: e.target.value })}
                          className={`${inputCls} w-56 bg-surface-card`} aria-label="Forecast category">
                          {FORECAST_CATEGORY_PICKER.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <span className="text-11 text-ink-faint font-normal max-w-56 text-right">
                          {propsDraft.forecastCategory === ''
                            ? 'Uncategorised — nobody has called this deal yet'
                            : 'Set on this deal'}
                        </span>
                      </span>
                    ) : (
                      <FrozenValue>{forecastCategoryLabel(deal.forecastCategory)}</FrozenValue>
                    )}
                  </PropRow>
                  <PropRow label="Expected close">
                    {fieldEditable('expectedClose', deal.stage) ? (
                      <input type="date" value={propsDraft.expectedClose}
                        onChange={e => setPropsDraft({ ...propsDraft, expectedClose: e.target.value })}
                        className={`${inputCls} w-44`} aria-label="Expected close" />
                    ) : (
                      <FrozenValue>{deal.expectedCloseLabel}</FrozenValue>
                    )}
                  </PropRow>
                  <PropRow label="Owner">
                    <OwnerSelect value={propsDraft.assignedEmployeeId}
                      onChange={id => setPropsDraft({ ...propsDraft, assignedEmployeeId: id })}
                      className={`${inputCls} w-56 bg-surface-card`}
                      inheritHint={deal.owner && !deal.assignedEmployeeId ? `Currently ${deal.owner}` : undefined} />
                  </PropRow>
                  <PropRow label="Source">
                    <select value={propsDraft.source} onChange={e => setPropsDraft({ ...propsDraft, source: e.target.value })}
                      className={`${inputCls} w-56 bg-surface-card`} aria-label="Source">
                      <option value="">{deal.source === '' ? 'No source' : '(keep current)'}</option>
                      {/* SB-6: a stored legacy value pre-selects for honesty; it rides
                          the wire only if the user actually changes it. */}
                      {deal.source !== '' && !SOURCE_OPTIONS.some(o => o.value === deal.source) && (
                        <option value={deal.source}>{deal.source}</option>
                      )}
                      {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </PropRow>
                  <PropRow label="Priority">
                    {fieldEditable('priority', deal.stage) ? (
                      <select value={propsDraft.priority} onChange={e => setPropsDraft({ ...propsDraft, priority: e.target.value })}
                        className={`${inputCls} w-56 bg-surface-card`} aria-label="Priority">
                        <option value="">{deal.priority === '' ? 'No priority' : '(keep current)'}</option>
                        {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <FrozenValue>{PRIORITY_LABEL[deal.priority] ?? '—'}</FrozenValue>
                    )}
                  </PropRow>
                  <PropRow label="Account">
                    <span className="block w-56">
                      <AccountPicker id="deal-edit-account"
                        value={{ accountId: propsDraft.accountId, company: propsDraft.accountName }}
                        onChange={p => setPropsDraft({ ...propsDraft, accountId: p.accountId, accountName: p.company })}
                        className={`${inputCls} w-full`} placeholder="Search accounts…" />
                    </span>
                  </PropRow>
                </>
              ) : (
                <>
              <PropRow label="Value"><span className="inline mr-1 text-ink-faint"><CurrencyGlyph code={deal.currency} /></span>{money(deal.amountPence / 100, deal.currency)}</PropRow>
              {/* 2.2 (P2-7): the effective value AND where it came from — a rep
                  must be able to tell "nobody assessed this, it inherits 50" from
                  "someone set it to 50". A closed deal with no assessment shows
                  nothing rather than a fabricated 100/0. */}
              <PropRow label="Probability">
                {deal.effectiveProbability != null ? (
                  <span className="inline-flex flex-col items-end gap-0.5">
                    <span>{deal.effectiveProbability}%</span>
                    <span className="text-11 text-ink-faint font-normal">{probabilitySourceLabel(deal.probabilitySource)}</span>
                  </span>
                ) : '—'}
              </PropRow>
              {/* 2.3a (P2-9): Uncategorised is a REAL state, not an empty cell —
                  a rep must see that nobody has categorised this deal, not that
                  the field is missing. So this row NEVER renders '—'; it renders
                  the word, muted, with the reason beside it. */}
              <PropRow label="Forecast category">
                {deal.forecastCategory != null ? (
                  <span>{forecastCategoryLabel(deal.forecastCategory)}</span>
                ) : (
                  <span className="inline-flex flex-col items-end gap-0.5">
                    <span className="text-ink-muted">{forecastCategoryLabel(null)}</span>
                    <span className="text-11 text-ink-faint font-normal">nobody has called this deal yet</span>
                  </span>
                )}
              </PropRow>
              <PropRow label="Expected close"><Calendar size={12} className="inline mr-1 text-ink-faint" />{deal.expectedCloseLabel}</PropRow>
              <PropRow label="Owner"><User size={12} className="inline mr-1 text-ink-faint" />{deal.owner || '—'}</PropRow>
              <PropRow label="Source"><Tag size={12} className="inline mr-1 text-ink-faint" />{deal.source ? sourceLabel(deal.source) : '—'}</PropRow>
              <PropRow label="Priority"><Flag size={12} className="inline mr-1 text-ink-faint" />{PRIORITY_LABEL[deal.priority] ?? '—'}</PropRow>
              {/* Slice A: the account link. Linked → the name opens the account
                  360; unlinked → the A2.3 picker (type-ahead + inline create)
                  writing accountId through PUT /deals/{id}. */}
              <PropRow label="Account">
                {linkedAccount ? (
                  onOpenAccount ? (
                    <button type="button" onClick={() => onOpenAccount(linkedAccount)} title="Open account"
                      className="font-medium hover:underline" style={{ color: accent.DEFAULT }}>
                      <Building2 size={12} className="inline mr-1" />{deal.company || 'Account'}
                    </button>
                  ) : (deal.company || 'Linked')
                ) : (
                  <button type="button" className="k-btn-quiet inline-flex items-center gap-1"
                    onClick={() => { setLinkPick({ accountId: '', company: deal.company }); setLinking(v => !v) }}>
                    <Link2 size={12} /> {linking ? 'Cancel' : 'Link account'}
                  </button>
                )}
              </PropRow>
              {linking && !linkedAccount && (
                <div className="py-3 border-b border-edge-row last:border-0">
                  <AccountPicker id="deal-link-account" value={linkPick} onChange={p => { void linkAccount(p) }}
                    className={`${inputCls} w-full`} placeholder="Search accounts…" autoFocus />
                  <div className="text-11 text-ink-faint mt-2">Pick an account to link it, or create one by name.</div>
                </div>
              )}
              {deal.issuedInvoiceId && <InvoiceRow invoiceId={deal.issuedInvoiceId} onNavigate={onNavigate} />}
              {/* SB-2: slug rendered via its label (legacy free text verbatim);
                  the note is the 'other' detail — shown whenever present. */}
              {deal.lostReason && (
                <PropRow label="Lost reason">
                  {lostReasonLabel(deal.lostReason)}
                  {deal.lostReasonNote && <span className="text-ink-secondary"> — {deal.lostReasonNote}</span>}
                </PropRow>
              )}
                </>
              )}
            </div>
          </div>

          <ContactCard contactId={deal.contactId} />
        </div>
      </div>

      {/* Close ceremony (§5.C/§5.E) — the response is the updated wire deal;
          mapping it through mapDealDetail refreshes stage, closedAt,
          lostReason, and (on a win) the invoice row in one apply. */}
      {closing && (
        <CloseDealModal
          deal={{ id: deal.id, title: deal.title, company: deal.company, amountPence: deal.amountPence, currency: deal.currency }}
          outcome={closing}
          onCancel={() => setClosing(null)}
          onClosed={wire => {
            applyDeal(mapDealDetail(wire))
            setClosing(null)
          }}
        />
      )}
    </div>
  )
}
